import { useState, useRef, useEffect } from "react";
import { Search, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Recherche d'adresse / lieu-dit (api-adresse.data.gouv.fr — gratuit, sans clé).
 * Biaisée sur le centre de La Réunion pour prioriser les résultats locaux.
 * Au clic d'un résultat, déclenche onSelect({ lat, lon, label, postcode }).
 */
const REUNION_BIAS = { lat: -21.115, lon: 55.536 };

export default function AddressSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAddresses = async (q) => {
    if (!q || q.trim().length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const url = new URL("https://api-adresse.data.gouv.fr/search/");
      url.searchParams.set("q", q);
      url.searchParams.set("limit", "8");
      url.searchParams.set("lat", REUNION_BIAS.lat);
      url.searchParams.set("lon", REUNION_BIAS.lon);
      const res = await fetch(url.toString());
      if (res.ok) {
        const data = await res.json();
        // Filtrer / prioriser les résultats Réunion (postcode 974xx)
        const features = (data.features || []).map(f => ({
          label: f.properties.label,
          city: f.properties.city,
          postcode: f.properties.postcode || "",
          context: f.properties.context,
          type: f.properties.type, // housenumber | street | locality | municipality
          lon: f.geometry.coordinates[0],
          lat: f.geometry.coordinates[1],
          isReunion: (f.properties.postcode || "").startsWith("974"),
        }));
        features.sort((a, b) => (b.isReunion - a.isReunion));
        setResults(features);
        setOpen(true);
      }
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchAddresses(v), 300);
  };

  const handleSelect = (item) => {
    setQuery(item.label);
    setOpen(false);
    onSelect?.(item);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="address-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5" style={{ color: "#4A4A4A" }} />
        <Input
          data-testid="address-search-input"
          placeholder="Adresse, lieu-dit, ville…"
          value={query}
          onChange={onChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-8 pr-8 h-9 text-sm bg-white border-black/10"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-2.5 w-3.5 h-3.5 animate-spin" style={{ color: "#2D6A4F" }} />
        )}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-xl border border-black/10 bg-white shadow-lg z-[9999]"
          data-testid="address-search-results"
        >
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lon}-${i}`}
              type="button"
              onClick={() => handleSelect(r)}
              data-testid={`address-result-${i}`}
              className="w-full text-left px-3 py-2 text-xs hover:bg-black/[0.03] border-b border-black/5 last:border-0 flex items-start gap-2"
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: r.isReunion ? "#2D6A4F" : "#9A9A9A" }} />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate" style={{ color: "#1A1A1A" }}>{r.label}</p>
                {r.context && (
                  <p className="text-[10px] truncate" style={{ color: "#7A7A7A" }}>{r.context}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && !loading && query.length >= 3 && results.length === 0 && (
        <div
          className="absolute left-0 right-0 mt-1 p-3 rounded-xl border border-black/10 bg-white shadow-lg z-[9999] text-xs italic text-center"
          style={{ color: "#7A7A7A" }}
          data-testid="address-search-empty"
        >
          Aucun résultat pour « {query} »
        </div>
      )}
    </div>
  );
}
